import {
  iconStudentButtonHeader,
  StudentCoursePlayerActivitiesSidebar,
  StudentCoursePlayerContentMock,
  StudentCoursePlayerHeader,
  useTheme,
  type QuizQuestionItem,
  type QuizSubmitAnswerPayload,
  type VimeoPlayerProgress,
  type StudentCoursePlayerActivityType,
  type StudentCoursePlayerModuleItem,
} from '@e-learning/shared';
import {
  createUserApi,
  createStudentApi,
  type StudentCourseProgressApiResponse,
  type StudentCoursePlayerApiResponse,
  type StudentCoursePlayerLesson,
  type StudentLessonDetailApiResponse,
  type StudentQuizAttemptApiResponse,
} from '@e-learning/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../../api/client';
import { authStorage } from '../../../auth';

const DEFAULT_PLAYER_COURSE_ID = 'd374bd93-6c33-4738-9476-1ea2ad579d1d';
const VIDEO_PROGRESS_SYNC_INTERVAL_MS = 30_000;
const studentApi = createStudentApi(api);
const userApi = createUserApi(api);

interface PlayerLessonView {
  id: string;
  title: string;
  duration: string;
  xp: string;
  status: 'done' | 'active' | 'locked';
  activityType: StudentCoursePlayerActivityType;
  vimeoUrl?: string;
  initialVideoTimeSeconds?: number;
  initialVideoProgressPercentage?: number;
}

interface DownloadableResourceView {
  fileName: string;
  sizeLabel: string;
  kind: 'pdf' | 'xlsx' | 'docx';
  fileUrl?: string;
}

function mapLessonType(type: StudentCoursePlayerLesson['type']): StudentCoursePlayerActivityType {
  if (type === 'FILE') return 'resource';
  if (type === 'QUIZ') return 'quiz';
  return 'video';
}

function mapLessonStatus(
  status: StudentCoursePlayerLesson['status']
): 'done' | 'active' | 'locked' {
  if (status === 'COMPLETED') return 'done';
  if (status === 'IN_PROGRESS') return 'active';
  return 'locked';
}

function lessonDurationLabel(): string {
  return '--';
}

function getFirstLessonId(modules: StudentCoursePlayerModuleItem[]): string | null {
  for (const module of modules) {
    if (module.lessons.length > 0) return module.lessons[0].id;
  }
  return null;
}

function findLessonById(
  lessonsById: Record<string, PlayerLessonView>,
  lessonId: string
) {
  return lessonsById[lessonId];
}

function toSidebarModules(
  data: StudentCoursePlayerApiResponse | null
): StudentCoursePlayerModuleItem[] {
  if (!data) return [];
  return data.modules
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(module => ({
      id: module.id,
      title: module.title,
      expanded: true,
      lessons: module.lessons
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(lesson => ({
          id: lesson.id,
          title:
            lesson.type === 'QUIZ' ? module.title : lesson.title,
          duration: lessonDurationLabel(),
          xp: `+${lesson.points}`,
          status: mapLessonStatus(lesson.status),
          activityType: mapLessonType(lesson.type),
        })),
    }));
}

function toLessonViewById(
  data: StudentCoursePlayerApiResponse | null
): Record<string, PlayerLessonView> {
  if (!data) return {};
  const entries = data.modules.flatMap(module =>
    module.lessons.map(lesson => [
      lesson.id,
      {
        id: lesson.id,
        title:
          lesson.type === 'QUIZ' ? module.title : lesson.title,
        duration: lessonDurationLabel(),
        xp: `+${lesson.points}`,
        status: mapLessonStatus(lesson.status),
        activityType: mapLessonType(lesson.type),
        vimeoUrl: lesson.videoUrl ?? undefined,
        initialVideoTimeSeconds: lesson.lastPosition ?? undefined,
        initialVideoProgressPercentage: lesson.progressPercentage ?? 0,
      } satisfies PlayerLessonView,
    ] as const)
  );
  return Object.fromEntries(entries);
}

function calculateProgressPercent(data: StudentCoursePlayerApiResponse | null): number {
  if (!data) return 0;
  const lessons = data.modules.flatMap(module => module.lessons);
  if (lessons.length === 0) return 0;
  const total = lessons.reduce(
    (sum, lesson) => sum + (lesson.progressPercentage ?? 0),
    0
  );
  return Math.round(total / lessons.length);
}

function formatDurationValue(hours: number | null | undefined): string {
  if (hours == null || Number.isNaN(hours)) return '--';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function formatBytesToLabel(sizeInBytes: number): string {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return '0 KB';
  const kb = sizeInBytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function toResourceKind(
  contentType: string | null | undefined,
  originalFileName: string
): 'pdf' | 'xlsx' | 'docx' {
  const type = (contentType ?? '').toLowerCase();
  const name = originalFileName.toLowerCase();
  if (type.includes('sheet') || name.endsWith('.xlsx')) return 'xlsx';
  if (type.includes('word') || name.endsWith('.docx')) return 'docx';
  return 'pdf';
}

function mapLessonFilesToResources(
  detail: StudentLessonDetailApiResponse | null | undefined
): DownloadableResourceView[] {
  if (!detail?.courseModuleLessonFiles?.length) return [];
  return detail.courseModuleLessonFiles.map(file => ({
    fileName: file.originalFileName,
    sizeLabel: formatBytesToLabel(file.sizeInBytes),
    kind: toResourceKind(file.contentType, file.originalFileName),
    fileUrl: file.fileUrl,
  }));
}

function mapQuizAttemptToQuestions(
  data: StudentQuizAttemptApiResponse
): QuizQuestionItem[] {
  return [...data.questions]
    .sort((a, b) => a.order - b.order)
    .map(q => ({
      prompt: q.title,
      options: q.options.map(o => o.description),
      questionId: q.questionId,
      optionIds: q.options.map(o => o.id),
    }));
}

function getOrderedLessonIds(
  data: StudentCoursePlayerApiResponse | null
): string[] {
  if (!data) return [];
  return data.modules
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .flatMap(m =>
      m.lessons
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(l => l.id)
    );
}

function getAdjacentLessonId(
  data: StudentCoursePlayerApiResponse | null,
  lessonId: string,
  direction: -1 | 1
): string | null {
  const ids = getOrderedLessonIds(data);
  const currentIndex = ids.indexOf(lessonId);
  if (currentIndex < 0) return null;
  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= ids.length) return null;
  return ids[nextIndex] ?? null;
}

export default function CoursesPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCourseId =
    searchParams.get('courseId')?.trim() || DEFAULT_PLAYER_COURSE_ID;
  const currentUserId = authStorage.getUserId();
  const currentUserEmail = authStorage.getEmail() ?? '-';
  const [currentUserName, setCurrentUserName] = useState<string>('Aluno');
  const [playerData, setPlayerData] = useState<StudentCoursePlayerApiResponse | null>(
    null
  );
  const [progressData, setProgressData] = useState<StudentCourseProgressApiResponse | null>(
    null
  );
  const [selectedLessonId, setSelectedLessonId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lessonFilesByLessonId, setLessonFilesByLessonId] = useState<
    Record<string, DownloadableResourceView[]>
  >({});
  const [resourceLoadingByLessonId, setResourceLoadingByLessonId] = useState<
    Record<string, boolean>
  >({});
  const lastVideoProgressSentRef = useRef<Record<string, number>>({});
  const lessonDetailCacheRef = useRef<Record<string, StudentLessonDetailApiResponse>>({});
  /** attemptId por lição após POST quiz-attempts (útil para envio de respostas no futuro). */
  const lessonQuizAttemptIdRef = useRef<Record<string, string>>({});

  useEffect(() => {
    setSelectedLessonId('');
  }, [selectedCourseId]);

  useEffect(() => {
    let cancelled = false;
    if (!currentUserId) {
      setCurrentUserName('Aluno');
      return;
    }

    userApi
      .fetchUserById(currentUserId)
      .then(user => {
        if (cancelled) return;
        const name = user?.name?.trim();
        setCurrentUserName(name || 'Aluno');
      })
      .catch(() => {
        if (cancelled) return;
        setCurrentUserName('Aluno');
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const loadCourseData = useCallback(
    async (options?: { activeRef?: { current: boolean }; silent?: boolean }) => {
      const silent = options?.silent === true;
      const activeRef = options?.activeRef;
      if (!silent) {
        setIsLoading(true);
        setErrorMessage(null);
      }
      try {
        const [playerResponse, progressResponse] = await Promise.all([
          studentApi.fetchCoursePlayer(selectedCourseId),
          studentApi.fetchCourseProgress(selectedCourseId),
        ]);
        if (activeRef && !activeRef.current) return null;
        setPlayerData(playerResponse);
        setProgressData(progressResponse);
        return { playerResponse, progressResponse };
      } catch {
        if (activeRef && !activeRef.current) return null;
        if (!silent) {
          setErrorMessage(t('student_course_player.page.error_load_course'));
        }
      } finally {
        if ((!activeRef || activeRef.current) && !silent) {
          setIsLoading(false);
        }
      }
      return null;
    },
    [selectedCourseId, t, i18n.language]
  );

  useEffect(() => {
    const activeRef = { current: true };
    void loadCourseData({ activeRef });
    return () => {
      activeRef.current = false;
    };
  }, [loadCourseData]);

  const modules = useMemo(() => toSidebarModules(playerData), [playerData]);
  const lessonById = useMemo(() => toLessonViewById(playerData), [playerData]);
  const progressPercent = useMemo(() => {
    if (progressData?.progressPercentage != null) {
      return progressData.progressPercentage;
    }
    return calculateProgressPercent(playerData);
  }, [progressData, playerData]);

  const isLastLessonInCourse = useMemo(() => {
    if (!playerData || !selectedLessonId) return false;
    const ids = getOrderedLessonIds(playerData);
    return ids.length > 0 && ids[ids.length - 1] === selectedLessonId;
  }, [playerData, selectedLessonId]);

  useEffect(() => {
    const fallbackLessonId = getFirstLessonId(modules);
    const nextLessonId = playerData?.lastAccessedLessonId ?? fallbackLessonId ?? '';
    if (!nextLessonId) return;
    if (!lessonById[nextLessonId]) return;
    setSelectedLessonId(current => (current ? current : nextLessonId));
  }, [playerData, modules, lessonById]);

  const selectedLesson = useMemo(
    () => findLessonById(lessonById, selectedLessonId),
    [lessonById, selectedLessonId]
  );

  const activityType = selectedLesson?.activityType ?? 'video';
  const selectedLessonResources = useMemo(() => {
    if (!selectedLessonId) return undefined;
    const files = lessonFilesByLessonId[selectedLessonId];
    return files && files.length > 0 ? files : undefined;
  }, [lessonFilesByLessonId, selectedLessonId]);
  const selectedResourceLoading = selectedLessonId
    ? resourceLoadingByLessonId[selectedLessonId] === true
    : false;

  const selectLesson = useCallback((lessonId: string) => {
    const lesson = lessonById[lessonId];
    if (!lesson || lesson.status === 'locked') return;
    setSelectedLessonId(lessonId);
  }, [lessonById]);

  const handleQuizNextLesson = useCallback(() => {
    if (!playerData || !selectedLessonId) return;
    const nextId = getAdjacentLessonId(playerData, selectedLessonId, 1);
    if (!nextId) return;
    selectLesson(nextId);
  }, [playerData, selectedLessonId, selectLesson]);

  const handlePreviousLesson = useCallback(() => {
    if (!playerData || !selectedLessonId) return;
    const previousId = getAdjacentLessonId(playerData, selectedLessonId, -1);
    if (!previousId) return;
    selectLesson(previousId);
  }, [playerData, selectedLessonId, selectLesson]);

  const handleQuizFinishCourse = useCallback(() => {
    navigate('/student', { state: { refreshStudentStats: true } });
  }, [navigate]);

  useEffect(() => {
    const lesson = lessonById[selectedLessonId];
    if (!lesson || lesson.activityType !== 'resource') return;
    if (lessonDetailCacheRef.current[lesson.id]) {
      const cached = lessonDetailCacheRef.current[lesson.id];
      setLessonFilesByLessonId(prev => ({
        ...prev,
        [lesson.id]: mapLessonFilesToResources(cached),
      }));
      return;
    }
    setResourceLoadingByLessonId(prev => ({ ...prev, [lesson.id]: true }));
    let active = true;
    studentApi
      .fetchLessonDetail(lesson.id)
      .then(detail => {
        if (!active) return;
        lessonDetailCacheRef.current[lesson.id] = detail;
        setLessonFilesByLessonId(prev => ({
          ...prev,
          [lesson.id]: mapLessonFilesToResources(detail),
        }));
      })
      .catch(() => {
        if (!active) return;
        setLessonFilesByLessonId(prev => ({ ...prev, [lesson.id]: [] }));
      })
      .finally(() => {
        if (!active) return;
        setResourceLoadingByLessonId(prev => ({ ...prev, [lesson.id]: false }));
      });
    return () => {
      active = false;
    };
  }, [lessonById, selectedLessonId]);

  const handleVideoProgress = useCallback(
    async (progress: VimeoPlayerProgress) => {
      const lesson = lessonById[selectedLessonId];
      if (!lesson || lesson.activityType !== 'video') return;
      const now = Date.now();
      const lastSent = lastVideoProgressSentRef.current[lesson.id] ?? 0;
      const shouldForceSync = progress.completed;
      if (!shouldForceSync && now - lastSent < VIDEO_PROGRESS_SYNC_INTERVAL_MS) return;
      lastVideoProgressSentRef.current[lesson.id] = now;
      try {
        await studentApi.updateLessonVideoProgress(lesson.id, {
          lastPosition: Math.round(progress.currentTime),
          progressPercentage: Math.round(progress.percentage),
        });
      } catch {
        // Mantém a experiência do player fluida mesmo em falha de persistência.
      }
    },
    [lessonById, selectedLessonId]
  );

  const handleMarkComplete = useCallback(async () => {
    const lesson = lessonById[selectedLessonId];
    if (!lesson) return;
    try {
      await studentApi.completeLesson(lesson.id);
      const refreshedData = await loadCourseData({ silent: true });
      const nextLessonId = getAdjacentLessonId(
        refreshedData?.playerResponse ?? playerData,
        lesson.id,
        1
      );
      if (nextLessonId) {
        setSelectedLessonId(nextLessonId);
      }
    } catch {
      // Sem bloqueio de UX; mantém dados atuais caso falhe.
    }
  }, [lessonById, selectedLessonId, loadCourseData, playerData]);

  const fetchQuizPreAttempt = useCallback(async () => {
    const lesson = lessonById[selectedLessonId];
    if (!lesson || lesson.activityType !== 'quiz') {
      throw new Error('Invalid lesson');
    }
    return studentApi.fetchQuizPreAttempt(lesson.id);
  }, [lessonById, selectedLessonId]);

  const fetchQuizQuestions = useCallback(async () => {
    const lesson = lessonById[selectedLessonId];
    if (!lesson || lesson.activityType !== 'quiz') {
      throw new Error('Invalid lesson');
    }
    const res = await studentApi.startQuizAttempt(lesson.id);
    lessonQuizAttemptIdRef.current[lesson.id] = res.attemptId;
    return mapQuizAttemptToQuestions(res);
  }, [lessonById, selectedLessonId]);

  const handleQuizSubmit = useCallback(
    async (answers: QuizSubmitAnswerPayload[]) => {
      const lesson = lessonById[selectedLessonId];
      if (!lesson || lesson.activityType !== 'quiz') {
        throw new Error('Invalid lesson');
      }
      const attemptId = lessonQuizAttemptIdRef.current[lesson.id];
      if (!attemptId) {
        throw new Error('No quiz attempt');
      }
      const result = await studentApi.submitQuiz(attemptId, { answers });
      /** Atualiza progresso sem `isLoading` — senão o player desmonta e a tela de resultado do quiz some. */
      void loadCourseData({ silent: true });
      return result;
    },
    [lessonById, selectedLessonId, loadCourseData]
  );

  return (
    <div
      style={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <StudentCoursePlayerHeader
        backLink={
          <Link
            to="/student"
            state={{ refreshStudentStats: true }}
            aria-label={t('student_course_player.page.back_aria')}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={iconStudentButtonHeader}
              alt=""
              width={20}
              height={20}
              aria-hidden
              style={{ display: 'block', objectFit: 'contain' }}
            />
          </Link>
        }
        academyName={t('student_course_player.page.academy_name')}
        academySubtitle={t('student_course_player.page.academy_subtitle')}
        searchPlaceholder={t('student_course_player.header.search_placeholder')}
        userName={currentUserName}
        userEmail={currentUserEmail}
      />

      <div
        style={{
          display: 'flex',
          gap: 0,
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0,
          flexWrap: 'wrap',
          boxSizing: 'border-box',
          padding: 0,
          flex: 1,
        }}
      >
        <div
          style={{
            flex: '1 1 740px',
            minWidth: 0,
          }}
        >
          {isLoading ? (
            <div style={{ padding: theme.spacing.lg }}>
              {t('student_course_player.page.loading_player')}
            </div>
          ) : errorMessage ? (
            <div
              style={{ padding: theme.spacing.lg, color: theme.palette.error.main }}
            >
              {errorMessage}
            </div>
          ) : !selectedLesson ? (
            <div style={{ padding: theme.spacing.lg }}>
              {t('student_course_player.page.no_lessons')}
            </div>
          ) : (
          <StudentCoursePlayerContentMock
            activityType={activityType}
            lessonTitle={selectedLesson.title}
            durationLabel={selectedLesson.duration}
            xpLabel={`${selectedLesson.xp} XP`}
            actionLabel={t('student_course_player.page.mark_complete_continue')}
            vimeoUrl={selectedLesson.vimeoUrl}
            initialVideoTimeSeconds={selectedLesson.initialVideoTimeSeconds}
            initialVideoProgressPercentage={
              selectedLesson.initialVideoProgressPercentage
            }
            resourceDescription={
              selectedResourceLoading
                ? t('student_course_player.page.loading_file_details')
                : undefined
            }
            downloadableResources={selectedLessonResources}
            quizFetchQuestions={
              activityType === 'quiz' ? fetchQuizQuestions : undefined
            }
            quizFetchPreAttempt={
              activityType === 'quiz' ? fetchQuizPreAttempt : undefined
            }
            quizOnSubmit={
              activityType === 'quiz' ? handleQuizSubmit : undefined
            }
            isLastLessonInCourse={
              activityType === 'quiz' ? isLastLessonInCourse : undefined
            }
            quizOnNextLesson={
              activityType === 'quiz' ? handleQuizNextLesson : undefined
            }
            quizOnFinishCourse={
              activityType === 'quiz' ? handleQuizFinishCourse : undefined
            }
            onVideoProgress={handleVideoProgress}
            onPreviousLesson={handlePreviousLesson}
            onMarkComplete={handleMarkComplete}
          />
          )}
        </div>
        <StudentCoursePlayerActivitiesSidebar
          progressPercent={progressPercent}
          progressHint={t('student_course_player.page.progress_hint')}
          modules={modules}
          selectedLessonId={selectedLessonId}
          onLessonSelect={selectLesson}
          aboutTitle={t('student_course_player.page.about_title')}
          aboutDescription={playerData?.title ?? '-'}
          categoryLabel={t('student_course_player.page.category_label')}
          categoryValue={playerData?.categoryName ?? '-'}
          durationLabel={t('student_course_player.page.duration_label')}
          durationValue={formatDurationValue(playerData?.duration)}
          totalXpLabel={t('student_course_player.page.total_xp_label')}
          totalXpValue={
            playerData?.totalPoints != null ? `${playerData.totalPoints} XP` : '-'
          }
        />
      </div>
    </div>
  );
}
